import { useState } from "react";
import { useTranslation } from "react-i18next";
import { createWatchlistItem } from "../../lib/watchlist";
import { OTTERFOLIO_THEME } from "./theme";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function WatchlistItemFormModal({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const [symbol, setSymbol] = useState("");
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [isin, setIsin] = useState("");
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState<"" | "FELS" | "RAKETE">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const canSubmit = symbol.length > 0 && name.length > 0 && currency.length === 3;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await createWatchlistItem({
        symbol,
        name,
        currency,
        isin: isin.trim() === "" ? null : isin.trim(),
        notes: notes.trim() === "" ? null : notes.trim(),
        investmentCategory: category === "" ? null : category,
      });
      // reset
      setSymbol("");
      setName("");
      setCurrency("EUR");
      setIsin("");
      setNotes("");
      setCategory("");
      onCreated();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: overlay dialog uses controlled visibility
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        display: "grid",
        placeItems: "center",
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: OTTERFOLIO_THEME.bg,
          border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
          padding: 20,
          borderRadius: 12,
          minWidth: 340,
          maxWidth: 420,
          boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          color: OTTERFOLIO_THEME.text,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16 }}>
          {t("otterfolio.watchlist.addButton", { defaultValue: "+ Watchlist-Item" })}
        </h2>
        <label style={{ fontSize: 12 }}>
          Symbol
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="AAPL"
            style={{ width: "100%", marginTop: 4 }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Apple Inc."
            style={{ width: "100%", marginTop: 4 }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          Currency
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            placeholder="USD"
            maxLength={3}
            style={{ width: "100%", marginTop: 4 }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          ISIN (optional)
          <input
            value={isin}
            onChange={(e) => setIsin(e.target.value)}
            maxLength={12}
            style={{ width: "100%", marginTop: 4 }}
          />
        </label>
        <label style={{ fontSize: 12 }}>
          Kategorie
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as "" | "FELS" | "RAKETE")}
            style={{ width: "100%", marginTop: 4 }}
          >
            <option value="">—</option>
            <option value="FELS">🪨 Fels</option>
            <option value="RAKETE">🚀 Rakete</option>
          </select>
        </label>
        <label style={{ fontSize: 12 }}>
          {t("otterfolio.watchlist.columnNotes", { defaultValue: "Notiz" })}
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            style={{ width: "100%", marginTop: 4 }}
          />
        </label>
        {error && <div style={{ color: OTTERFOLIO_THEME.highlight, fontSize: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button type="button" onClick={submit} disabled={busy || !canSubmit}>
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
